import React, { useState } from "react";
import { notification } from "antd";
import { FoCard, FoDialog, formatEgp, statusBadge } from "./financeUi";
import { os } from "./osClient";
import { PagerTable, SelectField, TextField, VendorSelect, useOsResource, useVendorOptions } from "./osForm";
import { formatStamp, nextPayoutStages, pageMeta } from "./osRules";

const STAGES = ["Created", "Reviewed", "Approved", "Processing", "Paid", "Failed", "Overdue", "Escalated"];
const blank = { vendorId: "", amount: "", cashCycle: "", dueDate: "", note: "" };

export default function PayoutWorkflow() {
  const vendors = useVendorOptions();
  const [stage, setStage] = useState("");
  const [vendorId, setVendorId] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [applied, setApplied] = useState({ stage: "", vendorId: "", startDate: "", endDate: "" });
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(20);
  const [open, setOpen] = useState(false);
  const [draft, setDraft] = useState(blank);
  const [noteFor, setNoteFor] = useState(null);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const list = useOsResource(
    (auth) => os.payouts(auth, { ...applied, page, limit }),
    [applied, page, limit]
  );
  const pack = pageMeta(list.data);

  const create = async () => {
    if (!draft.vendorId || !Number.isFinite(Number(draft.amount))) {
      notification.error({ message: "Brand and amount are required. A negative amount collects from the brand." });
      return;
    }
    setBusy(true);
    try {
      await os.createPayout(list.token, {
        vendorId: draft.vendorId,
        amount: Number(draft.amount),
        cashCycle: draft.cashCycle || undefined,
        dueDate: draft.dueDate || undefined,
        note: draft.note,
      });
      notification.success({ message: "Payout created. Money is recorded only when it reaches Paid." });
      setOpen(false);
      setDraft(blank);
      list.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  const move = async (row, nextStage, stageNote) => {
    setBusy(true);
    try {
      await os.movePayout(list.token, row.id || row._id, { stage: nextStage, note: stageNote || "" });
      notification.success({ message: nextStage === "Paid" ? "Payout recorded and emailed." : `Moved to ${nextStage}.` });
      setNoteFor(null);
      list.reload();
    } catch (err) {
      /* toast already shown */
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fo-stack">
      <div className="fo-tools">
        <SelectField label="Stage" value={stage} onChange={setStage} allowEmpty options={STAGES} />
        <VendorSelect vendors={vendors} value={vendorId} onChange={setVendorId} emptyLabel="All brands" />
        <TextField label="From" type="date" value={startDate} onChange={setStartDate} />
        <TextField label="To" type="date" value={endDate} onChange={setEndDate} />
        <button type="button" className="fo-btn" onClick={() => { setPage(1); setApplied({ stage, vendorId, startDate, endDate }); }}>Apply</button>
        <button type="button" className="fo-btn fo-btn-primary" onClick={() => setOpen(true)}>New payout</button>
      </div>
      <FoCard title="Payout stages" note="This workflow does not record money until the stage becomes Paid. The Add Payouts screen still pays immediately. Paid rows include legacy payouts; other stage filters do not.">
        <PagerTable
          loading={list.loading}
          dataSource={pack.docs}
          rowKey={(row) => row.id || row._id}
          page={page}
          limit={limit}
          total={pack.total}
          minWidth={980}
          expandable={{
            expandedRowRender: (row) => (
              <div className="fo-stack-sm">
                {(row.history || []).map((entry, index) => (
                  <div key={`${entry.stage}-${entry.at}-${index}`} className="fo-line">
                    <span>{statusBadge(entry.stage)}</span>
                    <span>{entry.actorName || "—"}</span>
                    <b>{formatStamp(entry.at)}</b>
                  </div>
                ))}
                {(row.history || []).length === 0 && <span className="fo-note">No stage history on this row.</span>}
              </div>
            ),
          }}
          onChange={(nextPage, nextLimit) => { setPage(nextPage); setLimit(nextLimit); }}
          empty="No payouts in this view."
          columns={[
            { title: "Brand", dataIndex: "brandName" },
            { title: "Amount", dataIndex: "amount", render: (value, row) => `${formatEgp(value)} EGP${row.isCollection ? " · collection" : ""}` },
            { title: "Stage", dataIndex: "stage", render: (value) => statusBadge(value) },
            { title: "By", dataIndex: "stageActorName", render: (name, row) => `${name || "—"} · ${formatStamp(row.stageAt)}` },
            { title: "Source", dataIndex: "source" },
            { title: "Due", dataIndex: "dueDate", render: (value) => value ? String(value).slice(0, 10) : "—" },
            {
              title: "",
              key: "actions",
              render: (_, row) => {
                if (!row.id || row.source === "legacy") return null;
                const moves = nextPayoutStages(row.stage);
                if (!moves.length) return null;
                return (
                  <div className="fo-tier-acts">
                    {moves.map((next) => (
                      <button key={next} type="button" className="fo-btn" disabled={busy} onClick={() => { setNote(""); setNoteFor({ row, next }); }}>{next}</button>
                    ))}
                  </div>
                );
              },
            },
          ]}
        />
      </FoCard>
      <FoDialog open={open} title="New staged payout" onClose={() => !busy && setOpen(false)}>
        <p className="fo-note">Starts at Created. Nothing is paid or emailed until someone moves it to Paid.</p>
        <div className="fo-form-grid">
          <VendorSelect vendors={vendors} value={draft.vendorId} onChange={(value) => setDraft({ ...draft, vendorId: value })} emptyLabel="Select a brand" />
          <TextField label="Amount" type="number" value={draft.amount} onChange={(amount) => setDraft({ ...draft, amount })} hint="Negative means the brand paid Chainz." />
          <TextField label="Cash cycle" value={draft.cashCycle} onChange={(cashCycle) => setDraft({ ...draft, cashCycle })} />
          <TextField label="Due date" type="date" value={draft.dueDate} onChange={(dueDate) => setDraft({ ...draft, dueDate })} />
          <TextField label="Note" value={draft.note} onChange={(value) => setDraft({ ...draft, note: value })} />
        </div>
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={create}>Create</button>
        </div>
      </FoDialog>
      <FoDialog open={Boolean(noteFor)} title={noteFor ? `Move to ${noteFor.next}` : ""} onClose={() => !busy && setNoteFor(null)}>
        <TextField label="Note" value={note} onChange={setNote} />
        <div className="fo-tier-acts" style={{ marginTop: 18 }}>
          <button type="button" className="fo-btn fo-btn-primary" disabled={busy} onClick={() => move(noteFor.row, noteFor.next, note)}>Confirm</button>
        </div>
      </FoDialog>
    </div>
  );
}
